import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Grid,
  Card,
  CardMedia,
  Chip,
  TextField,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Close, CalendarToday, Language, Star, AccessTime, Edit, Add, Flag, Share } from '@mui/icons-material';
import MovieActionButtons from './MovieActionButtons';
import ReportDialog from './ReportDialog';
import RecommendationsCarousel from './RecommendationsCarousel';
import PremiumMovieReviews from './PremiumMovieReviews';
import MovieNewsSection from './MovieNewsSection';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { useNotification } from '../../contexts/NotificationContext';
import { apiGet, apiPut } from '../../services/mongoApi';
import { getOTTPlatform, getPlatformButtonStyles, OTTIcon } from '../../utils/ottUtils';
import { trackMovieView, trackOTTClick } from '../../utils/analytics';
import { showAuthAlert } from '../../utils/authAlerts';
import { useNavigate } from 'react-router-dom';

const MovieDetailsDialog = ({ open, onClose, movie, movieId, movieData = null }) => {
  const { user } = useAuth();
  const { addNotification } = useApp();
  const { showError } = useNotification();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [fullMovieDetails, setFullMovieDetails] = useState(movieData);
  const [loading, setLoading] = useState(false);
  const [editingOtt, setEditingOtt] = useState(false);
  const [ottList, setOttList] = useState([]);
  const [ottInput, setOttInput] = useState('');
  const [ottReleaseDate, setOttReleaseDate] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  const [editingMovie, setEditingMovie] = useState(false);
  const [editedDetails, setEditedDetails] = useState({});
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [dominantColor, setDominantColor] = useState('#6366f1');
  const [refreshKey, setRefreshKey] = useState(0);
  const inputRef = useRef();
  
  const isAdmin = user?.role === 'ADMIN' || user?.user_type === 'admin';

  useEffect(() => {
    if (open && (movie || movieId || movieData)) {
      const movieToUse = movie || movieData;
      if (movieToUse) {
        const movieWithOttUrl = { ...movieToUse };
        if (movieToUse.ott && movieToUse.ott.length > 0) {
          movieWithOttUrl.ott_platform_url = movieToUse.ott[0];
        }
        setFullMovieDetails(movieWithOttUrl);
        setEditedDetails(movieWithOttUrl);
        setOttList(Array.isArray(movieToUse.ott) ? movieToUse.ott : []);
        setOttReleaseDate(movieToUse.ott_release_date || '');
      } else if (movieId) {
        fetchMovieDetails();
      }
    } else {
      setEditingMovie(false);
      setEditingOtt(false);
    }
  }, [open, movie, movieId, movieData]);

  const fetchMovieDetails = async () => {
    setLoading(true);
    try {
      const response = await apiGet(`/movies/${movieId}`);
      const movieWithOttUrl = { ...response };
      if (response.ott && response.ott.length > 0) {
        movieWithOttUrl.ott_platform_url = response.ott[0];
      }
      setFullMovieDetails(movieWithOttUrl);
      setEditedDetails(movieWithOttUrl);
      setOttList(Array.isArray(response.ott) ? response.ott : []);
      setOttReleaseDate(response.ott_release_date || '');
      
      trackMovieView(response.id, response.name);
    } catch (error) {
      console.error('Error fetching movie details:', error);
      setFullMovieDetails(null);
    } finally {
      setLoading(false);
    }
  };

  const handleAddOtt = () => {
    const url = ottInput.trim();
    if (!url) return;
    setOttList(prev => [...prev, url]);
    setOttInput('');
    inputRef.current?.focus();
  };
  
  const handleRemoveOtt = (idx) => {
    setOttList(prev => prev.filter((_, i) => i !== idx));
  };
  
  const handleSaveOtt = async () => {
    if (!fullMovieDetails?.id) return;
    setSaveLoading(true);
    try {
      await apiPut(`/movies/${fullMovieDetails.id}`, {
        ott: ottList,
        ott_release_date: ottReleaseDate
      });
      const updatedMovie = { ...fullMovieDetails, ott: ottList, ott_release_date: ottReleaseDate };
      if (ottList.length > 0) {
        updatedMovie.ott_platform_url = ottList[0];
      }
      setFullMovieDetails(updatedMovie);
      setEditingOtt(false);
    } catch (error) {
      console.error('Failed to update OTT info:', error);
      showError('Failed to update OTT info');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleUpdateMovieDetails = async () => {
    if (!fullMovieDetails?.id) return;
    setSaveLoading(true);
    try {
      const response = await apiPut(`/movies/${fullMovieDetails.id}`, editedDetails);
      setFullMovieDetails(response);
      setEditingMovie(false);
    } catch (error) {
      console.error('Failed to update movie details:', error);
      showError('Failed to update movie details');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleMovieDetailChange = (e) => {
    const { name, value } = e.target;
    setEditedDetails(prev => ({ ...prev, [name]: value }));
  };

  const handleClose = () => {
    setFullMovieDetails(null);
    onClose();
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
      sx={{ 
        zIndex: 1300,
        '& .MuiDialog-paper': {
          background: 'transparent',
          boxShadow: 'none',
          overflow: 'visible'
        }
      }}
    >
      <Box
        sx={{
          position: 'relative',
          borderRadius: 4,
          overflow: 'hidden',
          backdropFilter: 'blur(20px) saturate(180%)',
          background: theme.palette.mode === 'dark'
            ? `linear-gradient(135deg, 
                rgba(${dominantColor.replace('rgb(', '').replace(')', '')}, 0.15) 0%,
                rgba(0, 0, 0, 0.8) 50%,
                rgba(0, 0, 0, 0.9) 100%)`
            : '#ffffff',
          border: theme.palette.mode === 'dark'
            ? `1px solid rgba(255, 255, 255, 0.1)`
            : `1px solid rgba(0, 0, 0, 0.08)`,
          boxShadow: theme.palette.mode === 'dark'
            ? `0 25px 50px -12px rgba(0, 0, 0, 0.8),
               0 0 0 1px rgba(255, 255, 255, 0.05),
               inset 0 1px 0 rgba(255, 255, 255, 0.1)`
            : '0 8px 32px rgba(0, 0, 0, 0.12)',
          maxHeight: { xs: '100vh', sm: '90vh' },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `radial-gradient(ellipse at 30% 20%, 
              rgba(${dominantColor.replace('rgb(', '').replace(')', '')}, 0.2) 0%, 
              transparent 70%)`,
            pointerEvents: 'none',
            zIndex: 0
          }
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        ) : fullMovieDetails ? (
          <>
            {/* Header */}
            <DialogTitle sx={{ position: 'relative', zIndex: 1 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
                    {fullMovieDetails.name}
                  </Typography>
                  <Box display="flex" gap={1}>
                    {fullMovieDetails.release_date && (
                      <Chip
                        icon={<CalendarToday />}
                        label={new Date(fullMovieDetails.release_date).getFullYear()}
                        size="small"
                        sx={{
                          backdropFilter: 'blur(10px)',
                          background: theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.1)'
                            : 'rgba(255, 255, 255, 0.3)',
                          border: `1px solid ${theme.palette.mode === 'dark' 
                            ? 'rgba(255, 255, 255, 0.2)' 
                            : 'rgba(255, 255, 255, 0.5)'}`
                        }}
                      />
                    )}
                    {fullMovieDetails.language && (
                      <Chip
                        icon={<Language />}
                        label={fullMovieDetails.language}
                        size="small"
                        variant="outlined"
                        sx={{
                          backdropFilter: 'blur(10px)',
                          background: theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.05)'
                            : 'rgba(255, 255, 255, 0.2)',
                          borderColor: theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.2)'
                            : 'rgba(255, 255, 255, 0.4)'
                        }}
                      />
                    )}
                  </Box>
                </Box>
                <Box>
                  {isAdmin && (
                    <IconButton 
                      onClick={() => {
                        setEditingMovie(!editingMovie);
                        setEditedDetails(fullMovieDetails);
                      }}
                      sx={{ 
                        mr: 1, 
                        color: 'primary.main',
                        backdropFilter: 'blur(10px)',
                        background: theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.05)'
                          : 'rgba(0, 0, 0, 0.02)'
                      }}
                    >
                      <Edit />
                    </IconButton>
                  )}
                  <IconButton 
                    onClick={() => {
                      if (!user) {
                        showAuthAlert(addNotification, navigate, 'REPORT');
                        return;
                      }
                      setReportDialogOpen(true);
                    }}
                    sx={{ 
                      mr: 1, 
                      color: 'error.main',
                      backdropFilter: 'blur(10px)',
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.02)'
                    }}
                  >
                    <Flag />
                  </IconButton>
                  <IconButton 
                    onClick={handleClose}
                    sx={{
                      backdropFilter: 'blur(10px)',
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.02)'
                    }}
                  >
                    <Close />
                  </IconButton>
                </Box>
              </Box>
            </DialogTitle>

            {/* Content */}
            <DialogContent sx={{ 
              position: 'relative', 
              zIndex: 1, 
              p: 3,
              maxHeight: { xs: 'calc(100vh - 200px)', sm: 'calc(90vh - 200px)' },
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: 8
              },
              '&::-webkit-scrollbar-track': {
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                borderRadius: 4
              },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                borderRadius: 4,
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'
                }
              }
            }}>
              {editingMovie && isAdmin ? (
                <Box component="form" noValidate autoComplete="off" sx={{ mb: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField 
                        name="name" 
                        label="Movie Title" 
                        value={editedDetails.name || ''} 
                        onChange={(e) => setEditedDetails(prev => ({ ...prev, name: e.target.value }))}
                        fullWidth 
                        margin="normal"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backdropFilter: 'blur(10px)',
                            background: theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.05)'
                              : 'rgba(255, 255, 255, 0.3)'
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField 
                        name="summary" 
                        label="Plot Summary" 
                        value={editedDetails.summary || ''} 
                        onChange={(e) => setEditedDetails(prev => ({ ...prev, summary: e.target.value }))}
                        fullWidth 
                        multiline 
                        rows={4} 
                        margin="normal"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backdropFilter: 'blur(10px)',
                            background: theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.05)'
                              : 'rgba(255, 255, 255, 0.3)'
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField 
                        name="language" 
                        label="Language" 
                        value={editedDetails.language || ''} 
                        onChange={(e) => setEditedDetails(prev => ({ ...prev, language: e.target.value }))}
                        fullWidth 
                        margin="normal"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backdropFilter: 'blur(10px)',
                            background: theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.05)'
                              : 'rgba(255, 255, 255, 0.3)'
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField 
                        name="runtime" 
                        label="Runtime (minutes)" 
                        type="number" 
                        value={editedDetails.runtime || ''} 
                        onChange={(e) => setEditedDetails(prev => ({ ...prev, runtime: e.target.value }))}
                        fullWidth 
                        margin="normal"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backdropFilter: 'blur(10px)',
                            background: theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.05)'
                              : 'rgba(255, 255, 255, 0.3)'
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField 
                        name="genre" 
                        label="Genres (comma-separated)" 
                        value={editedDetails.genre || ''} 
                        onChange={(e) => setEditedDetails(prev => ({ ...prev, genre: e.target.value }))}
                        fullWidth 
                        margin="normal"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backdropFilter: 'blur(10px)',
                            background: theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.05)'
                              : 'rgba(255, 255, 255, 0.3)'
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                    <Button 
                      onClick={() => setEditingMovie(false)}
                      sx={{
                        backdropFilter: 'blur(10px)',
                        background: theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.05)'
                          : 'rgba(255, 255, 255, 0.3)'
                      }}
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleUpdateMovieDetails}
                      variant="contained" 
                      disabled={saveLoading}
                      sx={{
                        backdropFilter: 'blur(10px)'
                      }}
                    >
                      {saveLoading ? 'Saving...' : 'Save'}
                    </Button>
                  </Box>
                </Box>
              ) : (
              <Grid container spacing={3}>
                {/* Poster */}
                <Grid item xs={12} sm={4}>
                  <Card sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    backdropFilter: 'blur(10px)',
                    background: theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(255, 255, 255, 0.3)',
                    border: `1px solid ${theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.1)' 
                      : 'rgba(255, 255, 255, 0.5)'}`,
                    boxShadow: theme.palette.mode === 'dark'
                      ? '0 8px 32px rgba(0, 0, 0, 0.3)'
                      : '0 8px 32px rgba(0, 0, 0, 0.1)'
                  }}>
                    <CardMedia
                      component="img"
                      image={fullMovieDetails.poster || "/placeholder-movie.svg"}
                      alt={fullMovieDetails.name}
                      sx={{ 
                        height: 400, 
                        objectFit: 'cover',
                        filter: 'contrast(1.1) saturate(1.1)'
                      }}
                      onLoad={(e) => {
                        if (fullMovieDetails.poster && !fullMovieDetails.poster.includes('tmdb.org')) {
                          // Extract color from loaded image
                          const canvas = document.createElement('canvas');
                          const ctx = canvas.getContext('2d');
                          canvas.width = 100;
                          canvas.height = 100;
                          try {
                            ctx.drawImage(e.target, 0, 0, 100, 100);
                            const imageData = ctx.getImageData(50, 50, 1, 1);
                            const [r, g, b] = imageData.data;
                            setDominantColor(`rgb(${r}, ${g}, ${b})`);
                          } catch (error) {
                            // Silently fail
                          }
                        }
                      }}
                    />
                  </Card>

                  {/* Stats */}
                  <Box sx={{ 
                    mt: 2, 
                    p: 2, 
                    borderRadius: 2,
                    backdropFilter: 'blur(10px)',
                    background: theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(255, 255, 255, 0.3)',
                    border: `1px solid ${theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.1)' 
                      : 'rgba(255, 255, 255, 0.5)'}`
                  }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box textAlign="center">
                          <Star sx={{ color: 'gold', fontSize: 24 }} />
                          <Typography variant="h6" fontWeight="bold">
                            {fullMovieDetails.rating ? fullMovieDetails.rating.toFixed(1) : 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Rating
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box textAlign="center">
                          <AccessTime sx={{ color: 'primary.main', fontSize: 24 }} />
                          <Typography variant="h6" fontWeight="bold">
                            {fullMovieDetails.runtime ? `${fullMovieDetails.runtime}m` : 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Duration
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Action Buttons */}
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <MovieActionButtons key={refreshKey} movie={fullMovieDetails} />
                  </Box>
                </Grid>

                {/* Details */}
                <Grid item xs={12} sm={8}>
                  <Typography variant="h6" gutterBottom>
                    Plot Summary
                  </Typography>
                  <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                    {fullMovieDetails.summary || 'No plot summary available.'}
                  </Typography>

                  {/* Genres */}
                  {fullMovieDetails.genre && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        Genres
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {fullMovieDetails.genre.split(',').map((genre, index) => (
                          <Chip 
                            key={index} 
                            label={genre.trim()} 
                            variant="outlined" 
                            color="primary"
                            sx={{
                              backdropFilter: 'blur(10px)',
                              background: theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.05)'
                                : 'rgba(255, 255, 255, 0.3)',
                              borderColor: 'primary.main'
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* OTT Section */}
                  <Box sx={{ mb: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Typography variant="h6">
                        {ottList.length > 0 ? 'Watch Now' : 'Streaming'}
                      </Typography>
                      {isAdmin && (
                        <Button
                          size="small"
                          startIcon={editingOtt ? <Close /> : <Edit />}
                          onClick={() => setEditingOtt(!editingOtt)}
                        >
                          {editingOtt ? 'Cancel' : (ottList.length > 0 ? 'Edit' : 'Add')}
                        </Button>
                      )}
                    </Box>
                    
                    {ottReleaseDate && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Streaming from: {new Date(ottReleaseDate).toLocaleDateString()}
                      </Typography>
                    )}
                    
                    {!editingOtt && ottList.length > 0 && (
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {Array.isArray(ottList) && ottList.map((url, idx) => {
                          const platform = getOTTPlatform(url, theme);
                          const buttonStyles = getPlatformButtonStyles(platform, theme);
                          return (
                            <Button
                              key={idx}
                              variant="contained"
                              sx={buttonStyles}
                              startIcon={<OTTIcon platform={platform.name} size={18} />}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={async (e) => {
                                trackOTTClick(platform.name, fullMovieDetails.name);
                                
                                if (user?.id) {
                                  try {
                                    // Auto-mark as watched
                                    const { markAsWatched } = await import('../../services/movieInteractionApi');
                                    await markAsWatched(user.id, fullMovieDetails.id);
                                    
                                    addNotification({
                                      type: 'success',
                                      message: `Marked "${fullMovieDetails.name}" as watched`
                                    });
                                    
                                    // Track with correct action type for achievements
                                    const { trackAction } = await import('../../utils/optimizedTracking');
                                    await trackAction(fullMovieDetails.id, 'MOVIE', 'mark_watched', { id: user.id }, {
                                      source: 'ott_click',
                                      platform: platform.name,
                                      autoMarked: true
                                    });
                                    
                                    // Refresh button state
                                    setRefreshKey(prev => prev + 1);
                                  } catch (error) {
                                    console.error('Error auto-marking as watched:', error);
                                  }
                                }
                              }}
                            >
                              {platform.name}
                            </Button>
                          );
                        })}
                      </Box>
                    )}
                    
                    {!editingOtt && ottList.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        No streaming links available
                      </Typography>
                    )}
                    
                    {editingOtt && isAdmin && (
                      <Box>
                        <TextField
                          label="OTT Release Date"
                          type="date"
                          value={ottReleaseDate ? ottReleaseDate.substr(0, 10) : ''}
                          onChange={(e) => setOttReleaseDate(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          size="small"
                          sx={{ mb: 2, width: '200px' }}
                        />
                        
                        <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                          {Array.isArray(ottList) && ottList.map((url, idx) => (
                            <Chip
                              key={idx}
                              label={getOTTPlatform(url).name}
                              onDelete={() => handleRemoveOtt(idx)}
                              color="primary"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                        
                        <Box display="flex" gap={1} mb={2}>
                          <TextField
                            ref={inputRef}
                            value={ottInput}
                            onChange={(e) => setOttInput(e.target.value)}
                            placeholder="Add OTT URL (e.g. https://...)"
                            size="small"
                            fullWidth
                            onKeyPress={(e) => e.key === 'Enter' && handleAddOtt()}
                          />
                          <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={handleAddOtt}
                          >
                            Add
                          </Button>
                        </Box>
                        
                        <Button
                          variant="contained"
                          color="success"
                          onClick={handleSaveOtt}
                          disabled={saveLoading}
                        >
                          {saveLoading ? 'Saving...' : 'Save'}
                        </Button>
                      </Box>
                    )}
                  </Box>

                  {/* Cast & Crew */}
                  {(fullMovieDetails.hero || fullMovieDetails.heroine || fullMovieDetails.director || fullMovieDetails.music) && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        Cast & Crew
                      </Typography>
                      <Grid container spacing={2}>
                        {fullMovieDetails.hero && (
                          <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Lead Actor
                            </Typography>
                            <Typography variant="body1">{fullMovieDetails.hero}</Typography>
                          </Grid>
                        )}
                        {fullMovieDetails.heroine && (
                          <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Lead Actress
                            </Typography>
                            <Typography variant="body1">{fullMovieDetails.heroine}</Typography>
                          </Grid>
                        )}
                        {fullMovieDetails.director && (
                          <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Director
                            </Typography>
                            <Typography variant="body1">{fullMovieDetails.director}</Typography>
                          </Grid>
                        )}
                        {fullMovieDetails.music && (
                          <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Music Director
                            </Typography>
                            <Typography variant="body1">{fullMovieDetails.music}</Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  )}
                </Grid>



                {/* Reviews */}
                <Grid item xs={12}>
                  <Box sx={{ 
                    mt: 4, 
                    pt: 4, 
                    borderTop: `1px solid ${theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.1)' 
                      : 'rgba(0, 0, 0, 0.1)'}`
                  }}>
                    <PremiumMovieReviews movieId={fullMovieDetails.id} />
                  </Box>
                </Grid>

                {/* News */}
                <Grid item xs={12}>
                  <Box sx={{ 
                    mt: 4, 
                    pt: 4, 
                    borderTop: `1px solid ${theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.1)' 
                      : 'rgba(0, 0, 0, 0.1)'}`
                  }}>
                    <MovieNewsSection movieId={fullMovieDetails.id} movieName={fullMovieDetails.name} />
                  </Box>
                </Grid>

                {/* Recommendations */}
                <Grid item xs={12}>
                  <Box sx={{ 
                    mt: 4, 
                    pt: 4, 
                    borderTop: `1px solid ${theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.1)' 
                      : 'rgba(0, 0, 0, 0.1)'}`
                  }}>
                    <Typography variant="h6" gutterBottom>
                      {user ? 'Recommended for You' : 'Trending Movies'}
                    </Typography>
                    <RecommendationsCarousel 
                      currentMovieId={fullMovieDetails.id}
                      userId={user?.id}
                      isAuthenticated={!!user}
                      genres={fullMovieDetails.genre}
                      language={fullMovieDetails.language}
                      onMovieClick={(selectedMovie) => {
                        setFullMovieDetails(selectedMovie);
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
              )}
            </DialogContent>

            {/* Actions */}
            <DialogActions sx={{ 
              position: 'relative', 
              zIndex: 1,
              p: 3,
              borderTop: `1px solid ${theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.1)' 
                : 'rgba(0, 0, 0, 0.1)'}`
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <Button 
                  variant="outlined" 
                  startIcon={<Share />}
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({
                        title: fullMovieDetails.name,
                        text: `Check out ${fullMovieDetails.name} on HypeCircle!`,
                        url: window.location.href
                      });
                    } else {
                      navigator.clipboard.writeText(window.location.href);
                      addNotification({ type: 'success', message: 'Link copied to clipboard!' });
                    }
                  }}
                  sx={{
                    backdropFilter: 'blur(10px)',
                    background: theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(255, 255, 255, 0.3)',
                    borderColor: theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.2)'
                      : 'rgba(0, 0, 0, 0.2)'
                  }}
                >
                  Share
                </Button>
                <Button 
                  onClick={handleClose}
                  sx={{
                    backdropFilter: 'blur(10px)',
                    background: theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(255, 255, 255, 0.3)'
                  }}
                >
                  Close
                </Button>
              </Box>
            </DialogActions>
          </>
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography>Unable to load movie details</Typography>
            <Button onClick={handleClose} sx={{ mt: 2 }}>Close</Button>
          </Box>
        )}
      </Box>
      
      <ReportDialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        item={fullMovieDetails}
        type="movie"
      />
    </Dialog>
  );
};

export default MovieDetailsDialog;